// Правила формы «Задания по темам»: что можно выбрать, сколько заданий
// разрешено и из чего они соберутся (5.5 и 5.6 docs/homework-phase2-design.md).
//
// Почему модуль лежит здесь, а не в `apps/web`: это чистые правила, а у
// `@edu/web` бегунка тестов нет (раздел 9 CLAUDE.md). Проверять надо именно их:
// потолок количества берётся из двух мест сразу (потолок темы и остаток общего
// потолка домашки), а предпросмотр состава обязан совпадать с тем, что соберёт
// сервер, иначе экран показывает одно, а ученик получает другое.
//
// ⚠️ Реестра генераторов здесь нет и быть не может: на вход идёт справочник
// тем, то есть данные с сервера (5.11 фазы 2). Функции ничего не знают про
// правильные ответы и не могут их узнать.

import type { OgeTopicPick } from "../homework.ts";
import type { LessonBatchRow } from "../lesson-batch.ts";
import { planPicks } from "./plan-picks.ts";
import type { OgeTopic, OgeTopicGenerator, OgeTopicLevel } from "./topics.ts";

/**
 * Умолчание количества заданий по теме. Домашка на пять заданий это привычный
 * объём между занятиями, а «одно» никто не задаёт.
 */
export const DEFAULT_TOPIC_COUNT = 5;

/**
 * Строка-пик в форме: тема, уровень и количество.
 *
 * `rowId` придумывает клиент и он никуда не уезжает: строк по одной теме может
 * быть две (сервер их сложит), поэтому ключом строки не может быть тема.
 */
export interface TopicPickRow {
  readonly rowId: string;
  /** Пустая строка означает «тема ещё не выбрана»: строка добавлена, выбор нет. */
  readonly skill: string;
  /** `null` только у строки без темы: с темой уровень есть всегда. */
  readonly difficulty: number | null;
  readonly count: number;
}

export function findTopic(
  topics: readonly OgeTopic[], skill: string,
): OgeTopic | null {
  return topics.find((t) => t.skill === skill) ?? null;
}

export function findLevel(
  topic: OgeTopic, difficulty: number | null,
): OgeTopicLevel | null {
  if (difficulty === null) return null;
  return topic.levels.find((l) => l.difficulty === difficulty) ?? null;
}

/**
 * Умолчание уровня при выборе темы.
 *
 * ⚠️ **Решение ПРИНЯТО 20.08.2026 и здесь НЕ применено** (11.2 фазы 2, вопрос Б).
 * Владелец выбрал третий вариант: умолчания нет вовсе, тема не добавляется без
 * явного выбора уровня. Применение отложено им же 19.09.2026, и спрашивать
 * заново не надо: вопрос закрыт, ждёт только работа.
 *
 * Здесь пока стоит промежуточный вариант, названный проектом: самый низкий из
 * имеющихся у темы.
 * Он единственный, который не может дать ученику неподъёмную задачу без ведома
 * преподавателя. Два других варианта («средний, а где его нет, ближайший» и
 * «умолчания нет вовсе») меняют ровно эту функцию и ничего больше: у семи тем
 * из одиннадцати уровень единственный, и разницы между вариантами там нет.
 */
export function defaultDifficulty(topic: OgeTopic): number {
  // Уровни в справочнике по возрастанию (контракт 4.1), но полагаться на
  // порядок ответа тут незачем: минимум считается за ту же строку
  return topic.levels.reduce(
    (low, l) => (l.difficulty < low ? l.difficulty : low),
    topic.levels[0]?.difficulty ?? 1,
  );
}

/**
 * Строка, которая уедет на сервер: тема выбрана, значит выбран и уровень.
 *
 * ⚠️ Строка без темы не считается НИГДЕ, и это не мелочь. У добавленной строки
 * количество равно умолчанию (5), и складывать его со всеми значило бы показать
 * «всего заданий: 15 из 50» там, где уедет 10, и молча отнять пять у потолка
 * соседней строки. Предикат тот же, по которому строки отбирает `toTopicPicks`:
 * два разных ответа на вопрос «эта строка часть заказа?» разъедутся.
 */
function chosen(row: TopicPickRow): row is TopicPickRow & { difficulty: number } {
  return row.skill !== "" && row.difficulty !== null;
}

/** Строки об одном и том же: сервер сложит их в один пик (`mergePicks`). */
function sameTopic(a: TopicPickRow, b: TopicPickRow): boolean {
  return chosen(a) && chosen(b) && a.skill === b.skill && a.difficulty === b.difficulty;
}

/** Всего заданий по выбранным строкам: то самое «всего заданий: 5 из 50». */
export function picksTotal(rows: readonly TopicPickRow[]): number {
  return rows.reduce((sum, r) => (chosen(r) ? sum + r.count : sum), 0);
}

/**
 * Сколько заданий закажется по паре «тема и уровень» этой строки: сумма ВСЕХ
 * строк с той же парой, а не количество одной строки.
 *
 * ⚠️ Это правило сервера, а не украшение формы. `mergePicks` складывает пики с
 * одинаковой парой перед прогоном генераторов, значит «Проценты, лёгкий, 7» и
 * вторая такая же строка это один заказ на 14, а не два по 7. Считать
 * построчно значит завести второе правило счёта: форма отпустит кнопку, а
 * сервер откажет уже в цикле по ученикам, когда отменять поздно.
 *
 * Своя строка складывается по значению, а не берётся из `rows`: звать функцию с
 * ещё не вставленной строкой законно, и найтись в списке она не обязана.
 */
export function mergedCount(rows: readonly TopicPickRow[], row: TopicPickRow): number {
  return rows.reduce(
    (sum, r) => (r.rowId !== row.rowId && sameTopic(r, row) ? sum + r.count : sum),
    row.count,
  );
}

/** Сколько строк формы сложатся в один пик вместе с этой. */
export function sameTopicRows(rows: readonly TopicPickRow[], row: TopicPickRow): number {
  return rows.reduce((n, r) => (r.rowId !== row.rowId && sameTopic(r, row) ? n + 1 : n), 1);
}

/**
 * Сколько заданий эта строка может взять: потолок темы и уровня, но не больше
 * остатка общего потолка домашки.
 *
 * Два потолка, а не один, и оба обязательны. Потолок темы это размер
 * пространства задач её генераторов: у процентов лёгкого уровня различных
 * задач всего 48, и «дай 50 по процентам» невыполнимо физически. Общий потолок
 * это смысл: домашка на 50 заданий уже не домашка.
 *
 * Из потолка темы вычитается то, что заказано по ней ДРУГИМИ строками: они
 * сложатся с этой в один пик, и потолок у них общий.
 */
export function maxCountFor(
  level: OgeTopicLevel, rows: readonly TopicPickRow[], rowId: string, hardCap: number,
): number {
  const self = rows.find((r) => r.rowId === rowId) ?? null;
  const others = rows.filter((r) => r.rowId !== rowId);
  const left = hardCap - picksTotal(others);
  const twins = self === null
    ? 0
    : others.reduce((sum, r) => (sameTopic(r, self) ? sum + r.count : sum), 0);
  return Math.max(1, Math.min(level.maxCount - twins, left));
}

/**
 * Почему строка не годится. Разбор, а не текст: слова показывает форма, и они
 * зависят от того, где стоит строка на экране.
 */
export type PickProblem =
  | { readonly kind: "no-topic" }
  | { readonly kind: "unknown-topic"; readonly skill: string }
  | { readonly kind: "no-level"; readonly available: readonly number[] }
  | {
      readonly kind: "over-max";
      readonly maxCount: number;
      /** Сколько заказано по этой теме и уровню ВСЕМИ строками вместе. */
      readonly asked: number;
      /** Сколько таких строк. Больше одной значит, что виновата не эта одна. */
      readonly rows: number;
    }
  | { readonly kind: "over-total"; readonly total: number; readonly hardCap: number };

/**
 * ⚠️ Проверка стоит У СТРОКИ, а не под кнопкой (5.6, правило 4). Кнопка
 * блокируется, пока есть хоть одна негодная строка, но текст ошибки живёт возле
 * виноватой строки: под кнопкой человек не поймёт, какая из трёх тем виновата.
 *
 * ⚠️ Потолок темы сверяется с СУММОЙ строк по этой теме и уровню, а не с
 * количеством одной строки: разбор у `mergedCount`.
 */
export function rowProblem(
  topics: readonly OgeTopic[], rows: readonly TopicPickRow[], row: TopicPickRow, hardCap: number,
): PickProblem | null {
  if (row.skill === "") return { kind: "no-topic" };
  const topic = findTopic(topics, row.skill);
  if (topic === null) return { kind: "unknown-topic", skill: row.skill };
  const level = findLevel(topic, row.difficulty);
  if (level === null) {
    return { kind: "no-level", available: topic.levels.map((l) => l.difficulty) };
  }
  const asked = mergedCount(rows, row);
  if (!Number.isInteger(row.count) || row.count < 1 || asked > level.maxCount) {
    return {
      kind: "over-max",
      maxCount: level.maxCount,
      asked,
      rows: sameTopicRows(rows, row),
    };
  }
  const total = picksTotal(rows);
  if (total > hardCap) return { kind: "over-total", total, hardCap };
  return null;
}

/** Одна доля состава: сколько заданий даст конкретный генератор. */
export interface CompositionPart {
  readonly id: string;
  readonly title: string;
  readonly count: number;
  readonly answerKind: OgeTopicGenerator["answerKind"];
}

/**
 * Предпросмотр состава: из каких генераторов и по сколько соберётся домашка по
 * этой теме и этому уровню.
 *
 * Считается ТОЙ ЖЕ функцией `planPicks`, которой сервер потом разложит задания
 * по кругу, и по тому же порядку генераторов: порядок в справочнике это порядок
 * реестра, и это часть контракта 4.1. Отсортировать его «для красоты» на любой
 * из двух сторон значит показать преподавателю не тот состав, который приедет
 * ученику.
 *
 * Зачем предпросмотр вообще: состав фиксируется в момент создания и не
 * правится, а «3 задания среднего уровня по корням» это кот в мешке.
 *
 * ⚠️ `count` это СЛИТОЕ количество по теме и уровню (`mergedCount`), а не
 * количество одной строки. Иначе две строки «Корень, 5» показали бы дважды «3 и
 * 2», то есть пообещали бы 6 и 4, а сервер сложил бы их в 10 и разложил как 5 и
 * 5. Отказа при этом не будет вовсе, потолок темы 33: расхождение молча уедет
 * ученику, а состав домашки неизменяем.
 */
export function composition(level: OgeTopicLevel, count: number): CompositionPart[] {
  const byId = new Map(level.generators.map((g) => [g.id, g]));
  return planPicks(level.generators, count).flatMap((p) => {
    const gen = byId.get(p.generator);
    return gen === undefined
      ? []
      : [{ id: gen.id, title: gen.title, count: p.count, answerKind: gen.answerKind }];
  });
}

/**
 * Формы ответа в составе: одна на все задания или обе сразу.
 *
 * ⚠️ Смешанный состав это НЕ крайний случай и не поломка: у темы
 * «Арифметический квадратный корень» среднего уровня два генератора, и один
 * спрашивает вводом числа, другой выбором из вариантов. Четыре задания по этой
 * теме приедут ученику двумя парами разной формы. Преподавателю это говорится
 * вслух по той же причине, по которой 5.6 запрещает молчать про «5 заданий из 1
 * генератора»: он ожидал однородности, состав неизменяем, а узнаёт он о нём
 * иначе от ученика.
 */
export function answerKindsOf(parts: readonly CompositionPart[]): {
  readonly mixed: boolean;
  readonly number: number;
  readonly choice: number;
} {
  let numbers = 0;
  let choices = 0;
  for (const p of parts) {
    if (p.answerKind === "number") numbers += p.count;
    else choices += p.count;
  }
  return { mixed: numbers > 0 && choices > 0, number: numbers, choice: choices };
}

/** Темы, сгруппированные по разделам: выпадающий список рисуется группами. */
export function topicsBySection(topics: readonly OgeTopic[]): {
  readonly section: string;
  readonly sectionName: string;
  readonly topics: readonly OgeTopic[];
}[] {
  const order: string[] = [];
  const bySection = new Map<string, OgeTopic[]>();
  for (const t of topics) {
    if (!bySection.has(t.section)) {
      bySection.set(t.section, []);
      order.push(t.section);
    }
    bySection.get(t.section)?.push(t);
  }
  return order.map((section) => {
    const group = bySection.get(section) ?? [];
    return {
      section,
      sectionName: group[0]?.sectionName ?? section,
      topics: group,
    };
  });
}

/**
 * Строки формы в тело запроса.
 *
 * ⚠️ Уровень уезжает ВСЕГДА, даже когда он у темы единственный и человеку его
 * не предлагали выбирать (5.6, правило 2). Контракт от этого не меняется:
 * меняется только то, что выбора из одного варианта на экране нет.
 */
export function toTopicPicks(rows: readonly TopicPickRow[]): OgeTopicPick[] {
  return rows.flatMap((r) =>
    chosen(r) ? [{ skill: r.skill, difficulty: r.difficulty, count: r.count }] : [],
  );
}

/**
 * Строки формы в строки заказа ПАЧКИ на уроке (`docs/room-exercise-batch-design.md`).
 *
 * ⚠️ Отдельная функция, а не `toTopicPicks` с приведением типа: у пика домашки
 * уровень по типу необязателен, а у строки пачки обязателен, и приведение было
 * бы враньём. Предикат при этом тот же приватный `chosen`, что у `toTopicPicks`:
 * два ответа на вопрос «эта строка часть заказа?» разъедутся.
 */
export function toLessonBatchRows(rows: readonly TopicPickRow[]): LessonBatchRow[] {
  return rows.flatMap((r) =>
    chosen(r) ? [{ skill: r.skill, difficulty: r.difficulty, count: r.count }] : [],
  );
}
